import { io, type Socket } from 'socket.io-client';
import type {
  ChatClientToServer,
  ChatServerToClient,
  ChatMessage,
  ConversationOpenAck,
  MessageSendAck,
} from '@makemypartner/shared';
import { supabase } from '@/lib/supabase';

/**
 * The chat socket, as a module-level singleton.
 *
 * ONE CONNECTION FOR THE WHOLE APP, not one per component. The header needs it
 * for unread badges, the messages page needs it for a thread, and a toast
 * needs it from anywhere — three components each opening their own socket
 * would mean three authenticated connections per tab and three copies of every
 * message.
 *
 * Same reasoning as the wallet store in lib/data/wallet.ts, and the same
 * mistake avoided: state that several components need belongs in one place,
 * not in whichever component happens to mount first.
 */

const CHAT_URL: string = import.meta.env.VITE_CHAT_URL ?? 'http://localhost:4001';

export type ChatSocket = Socket<ChatServerToClient, ChatClientToServer>;

let socket: ChatSocket | null = null;
let connectingFor: string | null = null;

/**
 * Connect, or return the existing connection.
 *
 * Keyed by access token: when Supabase rotates the token the old socket is
 * torn down and a new one opened, because the server verified the OLD token at
 * handshake time and has no way to be told about the new one.
 */
export async function getChatSocket(): Promise<ChatSocket | null> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;

  if (!token) {
    disconnectChat();
    return null;
  }

  if (socket?.connected && connectingFor === token) return socket;

  if (socket) {
    socket.disconnect();
    socket = null;
  }

  connectingFor = token;
  socket = io(CHAT_URL, {
    auth: { token },
    transports: ['websocket', 'polling'],
    // Mobile networks drop constantly; keep trying rather than leaving the
    // user staring at a chat that has quietly stopped working.
    reconnection: true,
    reconnectionDelay: 1_000,
    reconnectionDelayMax: 10_000,
  });

  return socket;
}

export function disconnectChat(): void {
  socket?.disconnect();
  socket = null;
  connectingFor = null;
}

/** The live socket if there is one. Never opens a connection. */
export const currentChatSocket = (): ChatSocket | null => socket;

/* ── Promise wrappers ─────────────────────────────────────────────────────── */

/**
 * socket.io's acks are callbacks. These turn the three that matter into
 * awaitables, so a component reads as an ordinary async flow — and so a
 * missing ack becomes a rejected promise rather than a spinner that never
 * stops.
 */
const ACK_TIMEOUT_MS = 10_000;

function withAck<T>(run: (resolve: (value: T) => void) => void): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error('The chat server did not respond. Check your connection.')),
      ACK_TIMEOUT_MS,
    );
    run((value) => {
      clearTimeout(timer);
      resolve(value);
    });
  });
}

export async function openConversation(conversationId: string): Promise<ConversationOpenAck> {
  const s = await getChatSocket();
  if (!s) return { ok: false, code: 'not_authorized', message: 'Sign in to use chat.' };

  return withAck<ConversationOpenAck>((resolve) =>
    s.emit('conversation_open', { conversationId }, resolve),
  );
}

export async function sendMessage(
  conversationId: string,
  body: string,
  clientId: string,
): Promise<MessageSendAck> {
  const s = await getChatSocket();
  if (!s) return { ok: false, code: 'not_authorized', message: 'Sign in to use chat.' };

  return withAck<MessageSendAck>((resolve) =>
    s.emit('message_send', { conversationId, body, clientId }, resolve),
  );
}

export async function markConversationRead(conversationId: string): Promise<void> {
  const s = await getChatSocket();
  s?.emit('messages_read', { conversationId });
}

export async function setTyping(conversationId: string, isTyping: boolean): Promise<void> {
  const s = await getChatSocket();
  s?.emit('typing', { conversationId, isTyping });
}

export async function leaveConversation(conversationId: string): Promise<void> {
  const s = await getChatSocket();
  s?.emit('conversation_leave', { conversationId });
}

/** A client-side id, so an optimistic render can be reconciled with the echo. */
export const newClientId = (): string =>
  `c_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

export type { ChatMessage };
